// ══════════════════════════════════════════════════════
//  surfNameResolver.js — Surf 项目名多变体解析
//
//  问题背景：Surf news/feed 的 project 参数要求项目名精确匹配
//  Surf 的索引方式，否则返回 0 条。
//  例如："Humanity Protocol" → Surf 索引为 "humanity"，
//        但 fetchSurfNews 之前传 "humanity-protocol" 永远拿不到新闻。
//
//  本模块对外暴露 resolveSurfProjectName(name)：依次尝试 5 种变体，
//  首个返回 >0 结果的变体即胜出，并写入内存缓存（24h）。
//  这样无论 Hack/合作/上线哪类新闻，都不会因命名差异漏掉。
// ══════════════════════════════════════════════════════

import { RD_BASE } from './config';

// ── 内存缓存（24h TTL，命中 → 变体；未命中 → null）─────
const aliasCache = new Map();
const ALIAS_TTL  = 24 * 60 * 60 * 1000;

function getCached(name) {
  const e = aliasCache.get(name);
  if (!e) return undefined;
  if (Date.now() - e.ts > ALIAS_TTL) {
    aliasCache.delete(name);
    return undefined;
  }
  return e.variant; // 可能是 string 或 null
}

function setCached(name, variant) {
  aliasCache.set(name, { variant, ts: Date.now() });
}

// ── 变体生成器（按 Surf 命中概率排序）─────────────────
//
//  策略：从最具体（保留全名）到最宽松（去后缀首词）。
//  顺序保证：
//   1) "humanity protocol"   完整小写
//   2) "humanity-protocol"   连字符（最常见 slug）
//   3) "humanityprotocol"    无空格
//   4) "humanity"            去掉 Protocol/Labs/Network 等后缀
//   5) "humanity"            首词兜底（≥6 字符 + 非停用词时才启用）
//
//  EDGE Markets 教训：首词 "edge" 在 Surf 命中了 edgeX（错的项目），
//  污染新闻 / tokenomics / verdict 红绿旗。短词 + 通用英文词必须禁用兜底。
//  去重后实际尝试 3-5 个。
//
const FIRST_WORD_MIN_LEN = 6;
// 4 字符以下的通用英文词 + 一些常见 Web3 修饰词，作首词兜底容易撞错项目
const FIRST_WORD_STOPWORDS = new Set([
  'edge', 'wave', 'swift', 'dark', 'prime', 'core', 'max', 'omni', 'next',
  'alpha', 'beta', 'gamma', 'delta', 'omega',
  'ai', 'web3', 'crypto', 'super', 'mega', 'ultra', 'hyper', 'mini', 'micro',
  'blue', 'red', 'black', 'white', 'green', 'gold', 'silver',
  'meta', 'pro', 'plus', 'extra',
  'true', 'real', 'new', 'old',
  'trade', 'pay', 'cash', 'bank', 'fund',
]);

export function generateVariants(name) {
  if (!name || typeof name !== 'string') return [];
  const lower = name.toLowerCase().trim();
  if (!lower) return [];

  // 移除常见后缀（行业惯例：Protocol/Labs/Network/Finance/Foundation 等通常不在 Surf slug 里）
  const SUFFIX_RE = /\s+(protocol|labs?|network|finance|foundation|inc|ltd|llc|dao|club|swap|defi|fi|chain)$/i;
  const stripped = lower.replace(SUFFIX_RE, '').trim();
  const firstWord = stripped.split(/\s+/)[0] || '';

  // 安全门：单词形式的 stripped / firstWord 必须 ≥6 字符且非停用词才能作变体。
  // "Compound Finance" → "compound"（8 字符）✓
  // "Foo Labs" → "foo"（3 字符）✗
  // "Alpha Finance" → "alpha"（停用词）✗
  // 多词的 stripped 不受影响（已经够具体）。
  const isWordSafe = (w) => w.length >= FIRST_WORD_MIN_LEN && !FIRST_WORD_STOPWORDS.has(w);
  const strippedIsSingleWord = stripped && !stripped.includes(' ');
  const strippedSafe = strippedIsSingleWord
    ? (isWordSafe(stripped) ? stripped : '')
    : stripped; // 多词保留
  const firstWordSafe = isWordSafe(firstWord) ? firstWord : '';

  const candidates = [
    lower,                       // "humanity protocol"
    lower.replace(/\s+/g, '-'),  // "humanity-protocol"
    lower.replace(/\s+/g, ''),   // "humanityprotocol"
    strippedSafe,                // "humanity"（如果有后缀去掉了 + 通过安全门）
    firstWordSafe,               // "humanity"（仅 ≥6 字符 + 非停用词时启用）
  ].filter(v => v && v.length >= 2);

  // 去重，保留首次出现顺序
  return Array.from(new Set(candidates));
}

// ── 主入口：返回 Surf 上能命中的项目名（或 null）──────
//
//  调用方应该用返回的字符串去查 Surf；如果是 null 表示
//  Surf 暂无该项目数据，不要再发请求浪费 credits。
//
//  实现要点：
//   - 5 个变体**并行**探测，避免串行 5×5s = 25s 的最坏情况
//     （Surf 偶发 5s 长尾，多个变体串行直接撞 Railway 60s edge timeout）
//   - 全局 8s 软超时，超时也走负缓存，调用方拿到 null 继续走，不阻塞 analyze
//   - 首个返回 hits>0 的变体胜出（按 variants 数组顺序作为 tiebreaker）
//
const RESOLVER_TIMEOUT_MS = 8000;
const PROBE_TIMEOUT_MS    = 5000;

function probeWithTimeout(variant, signal) {
  return fetch(
    `${RD_BASE}/api/surf?path=/news/feed&project=${encodeURIComponent(variant)}&limit=1&sort_by=recency`,
    { signal }
  )
    .then(res => res.ok ? res.json() : null)
    .then(data => ({ variant, hits: data?.data?.length || 0 }))
    .catch(() => ({ variant, hits: 0 }));
}

export async function resolveSurfProjectName(originalName) {
  if (!originalName) return null;

  // 缓存命中（含负缓存）
  const cached = getCached(originalName);
  if (cached !== undefined) return cached;

  const variants = generateVariants(originalName);
  if (!variants.length) {
    setCached(originalName, null);
    return null;
  }

  // 并行探测所有变体；首个 hits>0 胜出（按 variants 顺序为 tiebreaker）
  // 全局 timeout：超过 8s 直接返回 null，不让 resolver 拖垮整体 analyze
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const probeTimer = setTimeout(() => controller?.abort(), PROBE_TIMEOUT_MS);

  try {
    const winnerPromise = Promise.all(
      variants.map(v => probeWithTimeout(v, controller?.signal))
    ).then(results => {
      // 按 variants 顺序（精确 > 模糊）找首个 hits>0
      for (const { variant, hits } of results) {
        if (hits > 0) return variant;
      }
      return null;
    });

    const winner = await Promise.race([
      winnerPromise,
      new Promise(resolve => setTimeout(() => resolve(null), RESOLVER_TIMEOUT_MS)),
    ]);

    setCached(originalName, winner);
    return winner;
  } finally {
    clearTimeout(probeTimer);
  }
}

// ── 测试 / 调试用：清空缓存 ──────────────────────────
export function clearSurfAliasCache() {
  aliasCache.clear();
}
